import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import {
  IResponseHandlerOptions,
  useResponseHandler,
} from 'utils/service/responseHandler';
import { ITemplatesLightListItem } from '../types';
import {
  IAgreementTemplateResponse,
  IAgreementTemplatetAggregator,
  IUpdateAgreementTemplateResponse,
} from './types';

export const useGetAgreementTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['agreement-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `AgreementTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetAgreementTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useGetAgreementTemplate = (
  id: string,
  options?: IResponseHandlerOptions<IAgreementTemplateResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IAgreementTemplateResponse, DefaultError>({
    queryKey: ['agreement-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `AgreementTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetAgreementTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostAgreementTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostAgreementTemplate'
  );
  return useMutation<string, DefaultError, IAgreementTemplatetAggregator>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'AgreementTemplate',
        body: { agreementTemplateAggregator: body },
      });

      return responseHandler(response, {
        groupName: 'usePostAgreementTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutAgreementTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutAgreementTemplate'
  );
  return useMutation<
    IUpdateAgreementTemplateResponse,
    DefaultError,
    IAgreementTemplatetAggregator
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'AgreementTemplate',
        body: { agreementTemplateId: id, agreementTemplateAggregator: body },
      });

      return responseHandler(response, {
        groupName: 'usePutAgreementTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.title,
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteAgreementTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteAgreementTemplate'
  );
  return useMutation<void, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `AgreementTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteAgreementTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
